import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCategories, selectModules } from "../../App/selectors";
import { selectAdData, setAdData } from "../../../routes/AdForm/adForm.ducks";
import styles from "./TitleWithModule.scss";
import NextButton from "./NextButton";
import history from "../../../history";
import { getWindowWidth, isMobileScreen, localStorage } from "../../../services";
import { CategoryNameHr, LocalStorageKey, ModuleNameHr } from "../../../models";
import { mapModuleToHr } from "../../../routes/LanguageMapper";
import { Form, Input, Select } from "antd";
import { requiredField } from "../../../models/formValidator";
import { forbiddenWords } from "../../../utils/ForbiddenWords";
import { selectUserInfo } from "../../../routes/Auth/auth.ducks";
import { selectIsAgeVerified } from "../../../routes/Ad/ad.ducks";
import AgeVerification from "../../../components/AgeVerificationModal";
import { PreviouslySelectedCategories } from "./PreviouslySelectedCategories";
import { useParamsFromTitle } from "./hooks/useParamsFromTitle";
// import { CategorySuggestions } from "./CategorySuggestions";

export interface ITitleWithModuleProps {
  title: string;
  match: any;
  draftLoaded: boolean;
  loading: boolean;
  settings: any;
  form: any;
}

export const defaultSettings = {
  formValidator: {
    title: [
      requiredField(null, "Naslov oglasa je obavezan"),
      {
        max: 100,
        message: "Maksimalan broj znakova u naslovu oglasa je 100."
      },
      {
        validator(rule, value, callback, source, options) {
          value && forbiddenWords.forEach(word => {
            if (value.toLowerCase().includes(word.toLowerCase())) {
              callback("Unijeli ste nedozvoljene riječi u naslov. Promijenite tekst ili nas kontaktirajte na 091/6089171.");
              return Promise.reject();
            }
          });
          return Promise.resolve();
        },
      }
    ],
    module: [requiredField(null, "Kategorija oglasa je obavezna.")],
    category: [requiredField(null, "Potkategorija oglasa je obavezna.")]
  }
};

const TitleWithModule = (props: ITitleWithModuleProps) => {
  const {
    title,
    match: { path },
    draftLoaded,
    settings: { formValidator },
    form,
  } = props;

  const [filteredCategories, setfilteredCategories] = useState([]);
  const [module, setModule] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const userData = useSelector(selectUserInfo);
  const isAgeVerified = useSelector(selectIsAgeVerified);
  const [ageVerificationModal, setAgeVerificationModal] = useState(false);

  const {
    fetchParamsFromTitle,
    isFetchingParamsFromTitle,
  } = useParamsFromTitle()

  const isMobile = isMobileScreen(getWindowWidth());

  const dispatch = useDispatch();

  const modules = useSelector(selectModules);
  const categories = useSelector(selectCategories);
  const adData = useSelector(selectAdData);

  // Use effect for loading draft data
  useEffect(() => {
    if (draftLoaded) {
      adData && adData.title && form.setFieldsValue({ title: adData.title });
      adData && adData.module && form.setFieldsValue({ module: mapModuleToHr(adData.module) });

      setfilteredCategories(
        categories.filter(c => c.moduleRoute == mapModuleToHr(adData.module))
      );

      const draftCategory = categories
        .filter(c => c.moduleRoute == adData.module || c.moduleName == adData.module)
        .filter(
          cat => cat.route == adData.category || cat.name == adData.category
        );

      form.setFieldsValue({ category: draftCategory[0].route });
    }
  }, [JSON.stringify(adData)]);

  // Filter categories
  const filterCategories = useCallback(module => {
    module && setfilteredCategories(categories.filter(c => c.moduleRoute == module));
  }, [categories]);

  // Handle submit
  const handleSubmit = useCallback(e => {
    e && e.preventDefault();
    setIsValidating(true);

    form.validateFields(async (error, values) => {
      setIsValidating(false);

      if (!error) {

        let fetchedParamsResponse = {}
        if (values.module === ModuleNameHr.vehicles && values.category === CategoryNameHr.car) { // atm fetching only for personal vehicles module
          const response = await fetchParamsFromTitle(values.title)
          fetchedParamsResponse = response ? response : {}
        }
        const tempDraft = localStorage.getItem(LocalStorageKey.savedForm);

        const tempAdData = {
          ...tempDraft,
          ...fetchedParamsResponse,
          ...values,
        };

        localStorage.setItem(LocalStorageKey.savedForm, tempAdData);
        dispatch(setAdData(tempAdData));

        history.push(`${values.module}${path}/${values.category}`);
      }
    });
  }, [form, localStorage, setAdData, history, userData]);

  // Handle module change
  const onModuleChange = useCallback(module => {
    setAgeVerificationModal(modules.filter(m => m.route === module && m.is18Old === true).length > 0);
    setModule(module);
    filterCategories(module);
    form.setFieldsValue({ category: null });
  }, [form, filterCategories]);

  const onCategoryChange = useCallback(category => {
    setAgeVerificationModal(filteredCategories.filter(c => c.route === category && c.is18Old === true).length > 0);
  }, [filteredCategories]);

  const orderCategories = (categories, module) => {
    if (module === ModuleNameHr.mobilePhones) {
      return [...categories].sort((a, b) => {
        const nameA = a.displayName.toUpperCase();
        const nameB = b.displayName.toUpperCase();
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      });
    } else if (module === ModuleNameHr.realEstate) {
      return [...categories];
    } else {
      return [...categories].sort((l1, l2) => l2.count - l1.count);
    }
  };

  const handleOnOk = useCallback(() => {
    setAgeVerificationModal(false);
  }, []);

  const handleOnCancel = useCallback(() => {
  }, [history, form]);

  return (
    <React.Fragment>
      {(ageVerificationModal && isAgeVerified !== true) && <AgeVerification onOk={handleOnOk} onCancel={handleOnCancel} />}
      <div className={styles.content}>
        {!title ? null : <div className={styles.title}>{title}</div>}
        <div className={styles.actions}>
          <Form layout="vertical">
            <p className={styles.subtitle}>{"Naslov oglasa"}</p>
            <Form.Item>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>

                {form.getFieldDecorator("title", {
                  rules: formValidator.title
                })(<Input
                  className={styles.input}
                  suffix={`${form.getFieldValue("title") ? form.getFieldValue("title").length : 0} / 100`}
                />)}
                {/* <CategorySuggestions
                  titleValue={form.getFieldValue("title")}
                  modules={modules}
                  categories={categories}
                  form={form}
                  onModuleChange={onModuleChange}
                  onCategoryChange={onCategoryChange}
                /> */}
              </div>
            </Form.Item>
            <PreviouslySelectedCategories
              modules={modules}
              categories={categories}
              form={form}
              onModuleChange={onModuleChange}
              onCategoryChange={onCategoryChange}
            />

            <div className={styles.dropDown}>
              <p className={styles.subtitle}>{"Odaberite kategoriju"}</p>
              <Form.Item>
                {form.getFieldDecorator("module", {
                  rules: formValidator.module
                })(<Select onChange={module => onModuleChange(module)}>
                  {modules &&
                    modules.map(option => (
                      <Select.Option
                        key={option.route}
                        value={option.route}
                        // @ts-ignore
                        className={styles.option}
                      >
                        {option.displayName}
                      </Select.Option>
                    ))}
                </Select>)}
              </Form.Item>
            </div>

            <div className={styles.dropDown}>
              <p className={!form.getFieldValue("module") ? styles.disabled : styles.subtitle}>{"Odaberi potkategoriju"}</p>
              <Form.Item>
                {form.getFieldDecorator("category", {
                  rules: formValidator.category
                })(<Select
                  disabled={!form.getFieldValue("module")}
                  onChange={onCategoryChange}
                >
                  {filteredCategories &&
                    orderCategories(filteredCategories, module).map(option => (
                      <Select.Option
                        key={option.id}
                        value={option.route}
                        // @ts-ignore
                        className={styles.option}
                      >
                        {option.displayName}
                      </Select.Option>
                    ))}
                </Select>)}
              </Form.Item>
            </div>
          </Form>

        </div>
        <NextButton
          onClick={handleSubmit}
          disabled={(ageVerificationModal && isAgeVerified !== true)}
          isEnable={true}
          isLoading={isValidating || isFetchingParamsFromTitle}
        />
      </div>
    </React.Fragment>
  );
};

TitleWithModule.defaultProps = {
  loading: false,
  settings: defaultSettings,
};

export default Form.create()(TitleWithModule)




// WEBPACK FOOTER //
// ./src/containers/SelectModulePage/TitleWithModule/index.tsx